import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Table } from "react-bootstrap";
import "./ReportsPage.css";

const ReportsPage = () => {
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filters, setFilters] = useState({ date: "", reportType: "" });

  // Sample data for reports
  useEffect(() => {
    const sampleReports = [
      {
        id: 1,
        title: "Daily Sales Trends",
        type: "sales",
        date: "2025-01-14",
        status: "Completed",
        salesData: {
          daily: "$2,000",
          monthly: "$50,000",
          yearly: "$600,000",
        },
      },
      {
        id: 2,
        title: "Top-Selling Products",
        type: "sales",
        date: "2025-01-13",
        status: "Completed",
        topProducts: [
          { name: "Pipe A", unitsSold: 300 },
          { name: "Pipe B", unitsSold: 250 },
        ],
      },
      {
        id: 3,
        title: "Inventory Turnover Rates",
        type: "inventory",
        date: "2025-01-12",
        status: "In Progress",
        turnoverRate: "15%",
      },
      {
        id: 4,
        title: "Order Processing Time",
        type: "operations",
        date: "2025-01-11",
        status: "Completed",
        processingTime: {
          average: "2 days",
          fastest: "1 day",
          slowest: "5 days",
        },
      },
    ];
    setReports(sampleReports);
    setFilteredReports(sampleReports);
  }, []);

  useEffect(() => {
    // Apply filters
    let filtered = reports;

    if (filters.date) {
      filtered = filtered.filter(
        (report) => new Date(report.date).toLocaleDateString() === filters.date
      );
    }

    if (filters.reportType) {
      filtered = filtered.filter(
        (report) => report.type === filters.reportType
      );
    }

    setFilteredReports(filtered);
  }, [filters, reports]);

  const handleViewReport = (report) => {
    setSelectedReport(report);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setSelectedReport(null);
    setShowModal(false);
  };

  const handleDownloadReport = (reportId) => {
    console.log(`Downloading report with ID: ${reportId}`);
  };

  return (
    <div className="reports-page">
      <div className="page-header">
        <h1>Ag Pipe Fitting Technology Reports</h1>
        <p>Manage and view various reports related to Ag Pipe fitting technology.</p>
      </div>

      {/* Filter Section */}
      <div className="filter-section">
        <Form>
          <Form.Group controlId="filterDate">
            <Form.Label>Filter by Date</Form.Label>
            <Form.Control
              type="date"
              value={filters.date}
              onChange={(e) => setFilters({ ...filters, date: e.target.value })}
            />
          </Form.Group>

          <Form.Group controlId="filterReportType">
            <Form.Label>Filter by Report Type</Form.Label>
            <Form.Control
              as="select"
              value={filters.reportType}
              onChange={(e) =>
                setFilters({ ...filters, reportType: e.target.value })
              }
            >
              <option value="">All Types</option>
              <option value="sales">Sales</option>
              <option value="inventory">Inventory</option>
              <option value="operations">Operations</option>
            </Form.Control>
          </Form.Group>
        </Form>
      </div>

      {/* Reports Table */}
      <Table striped bordered hover className="reports-table">
        <thead>
          <tr>
            <th>Report Title</th>
            <th>Type</th>
            <th>Date</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredReports.map((report) => (
            <tr key={report.id}>
              <td>{report.title}</td>
              <td>{report.type}</td>
              <td>{new Date(report.date).toLocaleDateString()}</td>
              <td>{report.status}</td>
              <td>
                <Button variant="info" onClick={() => handleViewReport(report)}>
                  View
                </Button>
                <Button
                  variant="success"
                  onClick={() => handleDownloadReport(report.id)}
                >
                  Download
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Modal for Report Details */}
      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>Report Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedReport && (
            <div>
              <h4>{selectedReport.title}</h4>
              <p>
                <strong>Report Type:</strong> {selectedReport.type}
              </p>
              <p>
                <strong>Date:</strong>{" "}
                {new Date(selectedReport.date).toLocaleDateString()}
              </p>
              <p>
                <strong>Status:</strong> {selectedReport.status}
              </p>

              {/* Report-Specific Details */}
              {selectedReport.salesData && (
                <div>
                  <h5>Sales Trends:</h5>
                  <p>
                    <strong>Daily:</strong> {selectedReport.salesData.daily}
                  </p>
                  <p>
                    <strong>Monthly:</strong> {selectedReport.salesData.monthly}
                  </p>
                  <p>
                    <strong>Yearly:</strong> {selectedReport.salesData.yearly}
                  </p>
                </div>
              )}

              {selectedReport.topProducts && (
                <div>
                  <h5>Top-Selling Products:</h5>
                  <ul>
                    {selectedReport.topProducts.map((product, index) => (
                      <li key={index}>
                        {product.name} - {product.unitsSold} units sold
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedReport.turnoverRate && (
                <div>
                  <h5>Inventory Turnover Rate:</h5>
                  <p>{selectedReport.turnoverRate}</p>
                </div>
              )}

              {selectedReport.processingTime && (
                <div>
                  <h5>Order Processing Time:</h5>
                  <p>
                    <strong>Average:</strong>{" "}
                    {selectedReport.processingTime.average}
                  </p>
                  <p>
                    <strong>Fastest:</strong>{" "}
                    {selectedReport.processingTime.fastest}
                  </p>
                  <p>
                    <strong>Slowest:</strong>{" "}
                    {selectedReport.processingTime.slowest}
                  </p>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ReportsPage;
